import clsx from 'clsx';
import { useMemo, useState } from 'react';

import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import {
  COMPRESSION_CHALLENGES,
  COMPRESSION_FIELDS,
  COMPRESSION_QUESTION,
  COMPRESSION_SUBSTANCE,
  DEFAULT_COMPRESSION_SETUP,
  DEFAULT_DENSITY_SETUP,
  DEFAULT_DIFFUSION_SETUP,
  DENSITY_CHALLENGES,
  DENSITY_FIELDS,
  DENSITY_PRESETS,
  DENSITY_QUESTION,
  DIFFUSION_CHALLENGES,
  DIFFUSION_FIELDS,
  DIFFUSION_QUESTION,
  DIFFUSION_SUBSTANCE,
  EXPLORE_TABS,
  exploreCompression,
  exploreDensity,
  exploreDiffusion,
  type CompressionExploreSetup,
  type DensityExploreSetup,
  type DiffusionExploreSetup,
  type ExploreTab,
} from './particleExplore';
import { CompressionComparison, DensityChart, DiffusionComparison } from './ParticleModelScenes';
import { type MatterState } from './particleModel';

const STATES: MatterState[] = ['gas', 'liquid', 'solid'];

function StatePicker({ label, value, names, onChange }: { label: string; value: MatterState; names: Record<MatterState, string>; onChange: (state: MatterState) => void }) {
  return (
    <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label={label}>
      {STATES.map((state) => (
        <button
          key={state}
          type="button"
          aria-pressed={value === state}
          onClick={() => onChange(state)}
          className={clsx(
            'rounded-full border px-3 py-1.5 text-xs font-extrabold',
            value === state ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink hover:border-brand-sky/50',
          )}
        >
          {names[state]}
        </button>
      ))}
    </div>
  );
}

function DensityExplorer() {
  const [setup, setSetup] = useState<DensityExploreSetup>(DEFAULT_DENSITY_SETUP);
  const result = useMemo(() => exploreDensity(setup), [setup]);
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="min-w-0">
        <ModelInputPanel legend="Your sample" fields={DENSITY_FIELDS} values={setup} onChange={setSetup} sliderKey="massG" />
        <div className="mt-3 flex flex-wrap gap-2" aria-label="Density presets">
          {DENSITY_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => setSetup(preset.setup)}
              className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky/50"
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <ExploreChallenges question={DENSITY_QUESTION} challenges={DENSITY_CHALLENGES} result={{ setup, result }} />
        </div>
        <div className="mt-4">
          <WorkedStepsPanel
            title="Density working"
            steps={result.steps}
            stepTestIdPrefix="density-step"
            result={result.result}
            resultTestId="density-result"
            limitNote={result.limitNote}
          />
        </div>
      </div>
      <div className="min-w-0">
        <DensityChart highlightIds={result.match ? [result.match.id] : []} sampleDensity={result.densityGPerCm3} />
      </div>
    </div>
  );
}

function CompressionExplorer() {
  const [state, setState] = useState<MatterState>('gas');
  const [setup, setSetup] = useState<CompressionExploreSetup>(DEFAULT_COMPRESSION_SETUP);
  const result = useMemo(() => exploreCompression(state, setup), [state, setup]);
  const syringes: MatterState[] = state === 'gas' ? ['gas', 'liquid'] : ['gas', state];
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="min-w-0">
        <StatePicker label="What is in the syringe" value={state} names={COMPRESSION_SUBSTANCE} onChange={setState} />
        <ModelInputPanel legend="Sealed syringe" fields={COMPRESSION_FIELDS} values={setup} onChange={setSetup} sliderKey="pressureMultiplier" />
        <div className="mt-4">
          <ExploreChallenges question={COMPRESSION_QUESTION} challenges={COMPRESSION_CHALLENGES} result={{ state, setup, result }} />
        </div>
        <div className="mt-4">
          <WorkedStepsPanel
            title="Compression working"
            steps={result.steps}
            stepTestIdPrefix="compression-step"
            result={result.result}
            resultTestId="compression-result"
            limitNote={result.limitNote}
          />
        </div>
      </div>
      <div className="min-w-0">
        <CompressionComparison startVolumeMl={setup.startVolumeMl} pressureMultiplier={setup.pressureMultiplier} states={syringes} />
        <p className="mt-2 text-xs font-medium leading-5 text-slate2">
          Air is always shown for comparison. Gas particles have large gaps, so squeezing pushes them closer; in liquids and
          solids the particles already touch.
        </p>
      </div>
    </div>
  );
}

function DiffusionExplorer({ time }: { time: number }) {
  const [state, setState] = useState<MatterState>('liquid');
  const [setup, setSetup] = useState<DiffusionExploreSetup>(DEFAULT_DIFFUSION_SETUP);
  const result = useMemo(() => exploreDiffusion(state, setup), [state, setup]);
  // Outside the model range the boxes keep the last valid picture while the working says why.
  const [shown, setShown] = useState(() => result.comparison);
  if (result.comparison && result.comparison !== shown) setShown(result.comparison);
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="min-w-0">
        <StatePicker label="Where the particles spread" value={state} names={DIFFUSION_SUBSTANCE} onChange={setState} />
        <ModelInputPanel legend="Conditions" fields={DIFFUSION_FIELDS} values={setup} onChange={setSetup} sliderKey="temperatureC" />
        <div className="mt-4">
          {/* Outside the 0–100 °C data the ratio is null, so no task can tick on an extrapolated value. */}
          <ExploreChallenges question={DIFFUSION_QUESTION} challenges={DIFFUSION_CHALLENGES} result={{ state, setup, result }} />
        </div>
        <div className="mt-4">
          <WorkedStepsPanel
            title="Diffusion working"
            steps={result.steps}
            stepTestIdPrefix="diffusion-step"
            result={result.result}
            resultTestId="diffusion-result"
            limitNote={result.limitNote}
          />
        </div>
      </div>
      <div className="min-w-0">
        {shown ? <DiffusionComparison first={shown.first} second={shown.second} time={time} /> : null}
        <p className="mt-2 text-xs font-medium leading-5 text-slate2">
          Dye spreading from the centre after the same time. Diffusion coefficients are typical orders of magnitude, not
          measurements.
        </p>
      </div>
    </div>
  );
}

/** Explore view (CT-INTERACT): the learner's own numbers for density, compression and diffusion. */
export function ParticleExploreTabs({ time }: { time: number }) {
  const [tab, setTab] = useState<ExploreTab>('density');
  return (
    <div>
      <div className="flex flex-wrap gap-1 rounded-full bg-surface p-1 sm:inline-flex" role="tablist" aria-label="Explore a particle model">
        {EXPLORE_TABS.map((item) => (
          <button
            key={item.id}
            id={`particle-explore-tab-${item.id}`}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            aria-controls="particle-explore-panel"
            onClick={() => setTab(item.id)}
            className={clsx(
              'rounded-full px-4 py-2 text-sm font-extrabold transition-colors',
              tab === item.id ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div id="particle-explore-panel" role="tabpanel" aria-labelledby={`particle-explore-tab-${tab}`} className="mt-4">
        {tab === 'density' ? <DensityExplorer /> : tab === 'compression' ? <CompressionExplorer /> : <DiffusionExplorer time={time} />}
      </div>
    </div>
  );
}
